import { AddSubscription, DataGridActionsColumn, TableView1 } from "..";
import React, { useContext, useMemo, useState } from "react";
import { ConfirmNotification } from "../Dialogs";
import { useDeleteSubscription, useSubscriptionsList } from "@/hooks";
import { useTextDomainContext } from "@/app/providers";
import { Button, Stack } from "@mui/material";
import { bool } from "prop-types";
import { useNavigate } from "react-router";

type SubscriptionsListProps = {
  parentPath: string;
  showAdd?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  showFeatures?: boolean;
};

type SubscriptionRow = {
  id?: string;
  name?: string;
  key?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  [key: string]: unknown;
};

type SubscriptionAction =
  "subscription-edit" | "subscription-delete" | "subscription-features";

type DeleteMultipleIds = Array<string | number>;

type DialogParameters = {
  action?: string;
  subscriptionPlanId?: string;
  ids?: DeleteMultipleIds;
};

type ConfirmResultPayload = {
  result?: string;
  parameters?: DialogParameters;
};

type ColumnValueGetterParams = {
  value?: string | number | Date | null;
};

export const SubscriptionsList = (props: SubscriptionsListProps) => {
  const navigate = useNavigate();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const parentPath = props.parentPath || "system-admin";

  // const deleting = false;
  const { deleteSubscriptionPlan, deleting } = useDeleteSubscription();

  const { subscriptionsList, loading } = useSubscriptionsList({});

  //region React Hook (useState)
  const [showConfirm, setShowConfirm] = useState(false);
  const [dialogParameters, setDialogParameters] = useState<DialogParameters>(
    {},
  );
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [editRow, setEditRow] = useState<SubscriptionRow>({});
  //endregion React Hook (useState)

  // Use props directly instead of state to avoid cascading renders
  const showAdd = props.showAdd;
  const showEdit = props.showEdit;
  const showDelete = props.showDelete;
  const showFeatures = props.showFeatures;

  //#region useMemo

  const showItems = useMemo(() => {
    return {
      showEdit: showEdit ?? false,
      showDelete: showDelete ?? false,
      showFeatures: showFeatures ?? false,
    };
  }, [showEdit, showDelete, showFeatures]);

  const showActionsColumn = useMemo(() => {
    return Object.values(showItems).some((item) => item === true);
  }, [showItems]);

  //#endregion useMemo

  //#region Handlers

  const menuClick = (action: SubscriptionAction, row: SubscriptionRow) => {
    switch (action) {
      case "subscription-edit":
        handleEdit(row);
        break;
      case "subscription-delete":
        handleOnClickDeleteSingle(row);
        break;
      case "subscription-features":
        handleOnClickFeatures(row);
        break;
      default:
        break;
    }
  };

  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
    columns: {
      columnVisibilityModel: {
        id: false,
        name: true,
        key: true,
        createdAt: false,
        updatedAt: false,
      },
    },
  };
  const columns = [
    {
      field: "id",
      headerName: gettext("ID"),
      checkboxSelection: true,
    },
    {
      field: "name",
      headerName: gettext("Subscription Plan"),
      flex: 1,
      // minWidth: 150,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "key",
      headerName: gettext("Key"),
      flex: 1,
      // minWidth: 150,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "createdAt",
      headerName: gettext("Created At"),
      headerAlign: "left",
      type: "date",
      valueGetter: ({ value }: ColumnValueGetterParams) =>
        value && new Date(value),
    },
    {
      field: "updatedAt",
      headerName: gettext("Updated At"),
      headerAlign: "left",
      type: "date",
      valueGetter: ({ value }: ColumnValueGetterParams) =>
        value && new Date(value),
    },
    ...DataGridActionsColumn(
      showActionsColumn,
      "subscription",
      showItems,
      menuClick,
    ),
  ];

  //region Handlers (continued)
  const handleEdit = (row: SubscriptionRow) => {
    setEditRow(row);
    setAddFormOpen(true);
  };

  const handleOnClickDeleteSingle = (row: SubscriptionRow) => {
    setDialogParameters({
      action: "delete-single",
      subscriptionPlanId: row.id,
    });
    setShowConfirm(true);
  };

  const handleOnClickDeleteMultiple = (ids: DeleteMultipleIds) => {
    setDialogParameters({
      action: "delete-multi",
      ids: ids,
    });
    setShowConfirm(true);
  };

  const handleGetResult = (obj: ConfirmResultPayload) => {
    if (obj.result === "yes") {
      deleteSubscriptionPlan({
        subscriptionPlanId: obj?.parameters?.subscriptionPlanId,
      });
    }
    setShowConfirm(false);
  };

  const handleOnClickFeatures = (row: SubscriptionRow) => {
    const key = row.key;
    navigate(`/${parentPath}/subscriptions/features/${key}`);
  };

  const postToolbar = () => {
    return (
      <>
        {showAdd && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ pl: 1, alignItems: "center" }}
          >
            <Button
              variant="contained"
              onClick={() => {
                setAddFormOpen(true);
              }}
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {gettext("Add subscription plan")}
            </Button>
          </Stack>
        )}
      </>
    );
  };

  const handleCloseAddForm = (event: boolean) => {
    setAddFormOpen(event);
    setEditRow({});
  };
  //endregion Handlers

  return (
    <>
      <TableView1
        title={gettext("Subscription Plans")}
        rows={subscriptionsList}
        columns={columns}
        loading={loading}
        checkboxSelection={false}
        initialState={initialState}
        postToolbar={postToolbar()}
        showGridToolbar={true}
        showGridToolbarDeleteButton={false}
        showGridToolbarDensitySelector={false}
        showGridToolbarColumnsButton={false}
        showGridToolbarQuickFilter
        showGridToolbarFilterButton={false}
        showGridToolbarExport={false}
        onClickDeleteMultiple={handleOnClickDeleteMultiple}
      />

      <AddSubscription
        open={addFormOpen}
        handleClose={handleCloseAddForm}
        row={editRow}
      />

      <ConfirmNotification
        isOpen={showConfirm}
        parameters={dialogParameters}
        getResult={handleGetResult}
      />
      {deleting ? (
        <div className="position-absolute bg-dark opacity-50 w-100 h-100 d-flex justify-content-center align-items-center">
          {/*<Spinner animation="border" variant="light" />*/}
        </div>
      ) : null}
    </>
  );
};

SubscriptionsList.propTypes = {
  showAdd: bool,
  showEdit: bool,
  showDelete: bool,
  showFeatures: bool,
};
